import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Button, message } from "antd";
import dayjs from "dayjs";
import { CaretDown, CaretRight, Compass } from "@phosphor-icons/react";
import {
  approveMatterCasePlan,
  getMatterCasePlan,
} from "../../service/casePlan";
import {
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  InboxItemBody,
  InboxItemRow,
  InboxListBody,
  InboxListEmptyIconRing,
  InboxListEmptyState,
  InboxListErrorBanner,
} from "../AIPane/AIPaneStyled";
import { WorkLaneHeader } from "../WorkItems/WorkItemsStyled";
import {
  PlanBranches,
  PlanChip,
  PlanChipRow,
  PlanField,
  PlanGroupToggle,
  PlanHint,
  PlanIssueTitle,
  PlanLinkButton,
  PlanLoopId,
  PlanStatusActions,
  PlanStatusBanner,
  PlanStatusText,
  PlanSummary,
  PlanTargetLine,
  PlanVersionRow,
} from "./CasePlanStyled";

/**
 * The Plan — the attorney's standing strategy for this matter, as data
 * (backend dms_backend #2577; design in whg-salesforce
 * docs/big-lawyer-build.md, "Let's build the big lawyer").
 *
 * What the panel shows, top to bottom:
 *   - Status banner: plan of record (who approved, when) / draft waiting
 *     for approval (with the Approve button) / stale (a letter broke it,
 *     with the reason and a link to that letter).
 *   - The summary in the attorney's voice; combined rating now → target.
 *   - Waiting on VA — the open loops (`L1`, `L2`, …): each filing we are
 *     waiting on, what we expect back, and the three branches. These are
 *     the ids Context+ names when a letter arrives ("this is the
 *     decision we were waiting on").
 *   - The plan, issue by issue — active issues first; won / parked /
 *     closed tucked under a toggle.
 *   - Versions — the history, newest first.
 *
 * Approving is the attorney's part: the memo engine proposes drafts, and
 * nothing is "of record" until a person clicks Approve here. The backend
 * refuses the fenced service account (403); the panel shows that reason
 * verbatim rather than guessing who the signed-in user is.
 */

const LANE_LABELS = Object.freeze({
  hlr: "Higher-Level Review",
  supplemental: "Supplemental claim",
  board: "Board appeal",
  wait: "Wait — filing pending",
  respond_to_request: "Respond to VA's request",
  new_claim: "New claim",
  cue: "Clear and unmistakable error",
  none: "No move yet",
});

const GOAL_LABELS = Object.freeze({
  "service connection": "service connection",
  increase: "increase",
  "earlier effective date": "earlier effective date",
  tdiu: "unemployability (TDIU)",
  smc: "special monthly compensation",
  "restore rating": "restore the rating",
  cue: "clear and unmistakable error",
  other: "",
});

const SOURCE_LABELS = Object.freeze({
  memo: "distilled from the strategy memo",
  manual: "written by an attorney",
  letter: "revised after an inbound letter",
});

const STATUS_LABELS = Object.freeze({
  approved: "approved",
  draft: "draft",
  superseded: "superseded",
});

export const laneLabel = (lane) => LANE_LABELS[lane] || LANE_LABELS.none;
export const goalLabel = (goal) =>
  Object.prototype.hasOwnProperty.call(GOAL_LABELS, goal)
    ? GOAL_LABELS[goal]
    : goal || "";

const longDate = (iso) => {
  if (!iso) return "";
  const d = dayjs(iso);
  return d.isValid() ? d.format("MMM D, YYYY") : String(iso);
};

const ratingText = (value) =>
  value == null || value === "" ? "" : `${value}%`;

/** Days from today to an ISO date; negative = past. */
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  return d.startOf("day").diff(dayjs().startOf("day"), "day");
};

const deadlineTone = (days) => {
  if (days == null) return "neutral";
  if (days < 0) return "over";
  if (days <= 30) return "flag";
  return "neutral";
};

const deadlineText = (deadline) => {
  if (!deadline || !deadline.last_day) return "";
  const days = daysUntil(deadline.last_day);
  const when = longDate(deadline.last_day);
  if (days == null) return when;
  if (days < 0) return `${when} — passed`;
  if (days === 0) return `${when} — today`;
  if (days === 1) return `${when} — tomorrow`;
  return `${when} — ${days} days`;
};

/* ------------------------------------------------------------------ */

const Branches = ({ ifGranted, ifDenied, ifPartial }) => {
  if (!ifGranted && !ifDenied && !ifPartial) return null;
  return (
    <PlanBranches>
      {ifGranted && (
        <>
          <dt>If granted</dt>
          <dd>{ifGranted}</dd>
        </>
      )}
      {ifDenied && (
        <>
          <dt>If denied</dt>
          <dd>{ifDenied}</dd>
        </>
      )}
      {ifPartial && (
        <>
          <dt>If partial</dt>
          <dd>{ifPartial}</dd>
        </>
      )}
    </PlanBranches>
  );
};

Branches.propTypes = {
  ifGranted: PropTypes.string,
  ifDenied: PropTypes.string,
  ifPartial: PropTypes.string,
};

export const OpenLoopCard = ({ loop, issuesByKey }) => {
  const conditions = (loop.issue_keys || [])
    .map((key) => issuesByKey[key]?.condition)
    .filter(Boolean);
  return (
    <InboxItemRow data-testid="plan-loop">
      <InboxItemBody>
        <PlanIssueTitle>
          <PlanLoopId aria-label={`Loop ${loop.id}`}>{loop.id}</PlanLoopId>
          {loop.filing || "Filing"}
          {loop.filed_on && <span>filed {longDate(loop.filed_on)}</span>}
        </PlanIssueTitle>
        {loop.expecting && (
          <PlanField>
            <strong>Expecting</strong>
            {loop.expecting}
          </PlanField>
        )}
        {conditions.length > 0 && (
          <PlanChipRow>
            {conditions.map((name) => (
              <PlanChip key={name}>{name}</PlanChip>
            ))}
          </PlanChipRow>
        )}
        <Branches
          ifGranted={loop.if_granted}
          ifDenied={loop.if_denied}
          ifPartial={loop.if_partial}
        />
      </InboxItemBody>
    </InboxItemRow>
  );
};

OpenLoopCard.propTypes = {
  loop: PropTypes.shape({
    id: PropTypes.string,
    filing: PropTypes.string,
    filed_on: PropTypes.string,
    expecting: PropTypes.string,
    issue_keys: PropTypes.arrayOf(PropTypes.string),
    if_granted: PropTypes.string,
    if_denied: PropTypes.string,
    if_partial: PropTypes.string,
  }).isRequired,
  issuesByKey: PropTypes.object,
};

export const IssueCard = ({ issue }) => {
  const goal = goalLabel(issue.goal);
  const deadlineDays = daysUntil(issue.deadline?.last_day);
  const waiting = issue.waiting_on || {};
  const isActive = issue.status === "active";
  return (
    <InboxItemRow data-testid="plan-issue" data-status={issue.status}>
      <InboxItemBody>
        <PlanIssueTitle>
          {issue.condition}
          {goal && <span>— {goal}</span>}
        </PlanIssueTitle>
        <PlanChipRow>
          {isActive ? (
            <PlanChip $tone="lane">{laneLabel(issue.lane_now)}</PlanChip>
          ) : (
            <PlanChip $tone={issue.status === "won" ? "won" : "neutral"}>
              {issue.status}
            </PlanChip>
          )}
          {issue.deadline?.last_day && (
            <PlanChip
              $tone={deadlineTone(deadlineDays)}
              title={issue.deadline.rule || undefined}
            >
              Last day {deadlineText(issue.deadline)}
            </PlanChip>
          )}
        </PlanChipRow>
        {issue.step && (
          <PlanField>
            <strong>Where it stands</strong>
            {issue.step}
          </PlanField>
        )}
        {issue.theory && (
          <PlanField>
            <strong>Theory</strong>
            {issue.theory}
          </PlanField>
        )}
        {(waiting.filing || waiting.expecting) && (
          <PlanField>
            <strong>Waiting on</strong>
            {[
              waiting.filing,
              waiting.filed_on ? `filed ${longDate(waiting.filed_on)}` : "",
              waiting.expecting ? `expecting ${waiting.expecting}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </PlanField>
        )}
        {Array.isArray(issue.evidence_needed) &&
          issue.evidence_needed.length > 0 && (
            <PlanField>
              <strong>Evidence still needed</strong>
              <ul>
                {issue.evidence_needed.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </PlanField>
          )}
        {issue.deadline?.rule && !issue.deadline?.last_day && (
          <PlanField>
            <strong>Clock</strong>
            {issue.deadline.rule}
          </PlanField>
        )}
        <Branches
          ifGranted={issue.if_granted}
          ifDenied={issue.if_denied}
          ifPartial={issue.if_partial}
        />
      </InboxItemBody>
    </InboxItemRow>
  );
};

IssueCard.propTypes = {
  issue: PropTypes.shape({
    key: PropTypes.string,
    condition: PropTypes.string,
    goal: PropTypes.string,
    lane_now: PropTypes.string,
    step: PropTypes.string,
    theory: PropTypes.string,
    evidence_needed: PropTypes.arrayOf(PropTypes.string),
    if_granted: PropTypes.string,
    if_denied: PropTypes.string,
    if_partial: PropTypes.string,
    deadline: PropTypes.shape({
      last_day: PropTypes.string,
      rule: PropTypes.string,
    }),
    status: PropTypes.string,
    waiting_on: PropTypes.shape({
      filing: PropTypes.string,
      filed_on: PropTypes.string,
      expecting: PropTypes.string,
    }),
  }).isRequired,
};

/* ------------------------------------------------------------------ */

const statusTone = (plan) => {
  if (plan.stale) return "stale";
  if (plan.status === "draft") return "draft";
  return "approved";
};

const PlanBanner = ({ plan, approving, onApprove, onOpenLetter }) => {
  const tone = statusTone(plan);
  const source = SOURCE_LABELS[plan.source_kind] || "";
  const isDraft = plan.status === "draft";

  let headline;
  if (tone === "stale") {
    headline = (
      <>
        <strong>A letter changed this plan.</strong>{" "}
        {plan.stale_reason ||
          "The last letter read against it did not fit any branch."}{" "}
        {plan.stale_document_id && onOpenLetter && (
          <PlanLinkButton
            type="button"
            onClick={() => onOpenLetter(plan.stale_document_id)}
          >
            Open the letter
          </PlanLinkButton>
        )}{" "}
        The memo is being refreshed; a new draft will land here for your
        approval.
        {isDraft &&
          " Until then this draft is the only plan on file — approve it only if it still holds."}
      </>
    );
  } else if (isDraft) {
    headline = (
      <>
        <strong>Draft v{plan.version} — waiting for your approval.</strong>{" "}
        {source ? `${source[0].toUpperCase()}${source.slice(1)}` : "Proposed"}
        {plan.created_at ? ` ${longDate(plan.created_at)}` : ""}
        {plan.created_by?.name ? ` by ${plan.created_by.name}` : ""}. Context+
        reads letters against a draft but labels it as such; approving makes
        it the plan of record.
      </>
    );
  } else {
    headline = (
      <>
        <strong>Plan of record v{plan.version}</strong>
        {plan.approved_by?.name ? ` — approved by ${plan.approved_by.name}` : ""}
        {plan.approved_at ? ` on ${longDate(plan.approved_at)}` : ""}
        {source ? ` · ${source}` : ""}.
      </>
    );
  }

  return (
    <PlanStatusBanner $tone={tone} role={tone === "stale" ? "alert" : "status"}>
      <PlanStatusText>{headline}</PlanStatusText>
      {isDraft && (
        <PlanStatusActions>
          <Button
            type="primary"
            size="small"
            loading={approving}
            onClick={onApprove}
            data-testid="plan-approve"
          >
            Approve this plan
          </Button>
        </PlanStatusActions>
      )}
    </PlanStatusBanner>
  );
};

PlanBanner.propTypes = {
  plan: PropTypes.object.isRequired,
  approving: PropTypes.bool,
  onApprove: PropTypes.func,
  onOpenLetter: PropTypes.func,
};

const VersionHistory = ({ versions, currentId }) => {
  if (!versions || versions.length === 0) return null;
  return (
    <>
      <WorkLaneHeader>Versions</WorkLaneHeader>
      {versions.map((v) => {
        const label = STATUS_LABELS[v.status] || v.status;
        const when =
          v.status === "approved" && v.approved_at
            ? `approved ${longDate(v.approved_at)}`
            : `${label} · ${longDate(v.created_at)}`;
        return (
          <PlanVersionRow key={v.id} data-testid="plan-version">
            <strong>v{v.version}</strong>
            <span>{when}</span>
            {SOURCE_LABELS[v.source_kind] && (
              <span>· {SOURCE_LABELS[v.source_kind]}</span>
            )}
            {v.stale && <PlanChip $tone="over">stale</PlanChip>}
            {v.id === currentId && <PlanChip $tone="lane">showing</PlanChip>}
          </PlanVersionRow>
        );
      })}
    </>
  );
};

VersionHistory.propTypes = {
  versions: PropTypes.arrayOf(PropTypes.object),
  currentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/* ------------------------------------------------------------------ */

const CasePlanPanel = ({ matterId, isActive = false }) => {
  const navigate = useNavigate();
  const [loadStatus, setLoadStatus] = useState("idle");
  const [payload, setPayload] = useState(null);
  const [approving, setApproving] = useState(false);
  const [showSettled, setShowSettled] = useState(false);
  // Monotonic request id: a slow answer for a previous matter must never
  // overwrite the current one (same guard as the Work panel).
  const requestSeq = useRef(0);
  // The matter on screen right now, readable from a callback that was
  // created for an earlier one.
  const matterRef = useRef(matterId);

  useEffect(() => {
    matterRef.current = matterId;
    // A matter switch retires any approve still in flight for the one we
    // left: its answer is ignored below and the button resets here.
    setApproving(false);
  }, [matterId]);

  const load = useCallback(() => {
    if (matterId == null) return;
    const seq = ++requestSeq.current;
    setLoadStatus("loading");
    getMatterCasePlan(matterId)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setPayload(response?.data || null);
        setLoadStatus("ready");
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setLoadStatus("failed");
      });
  }, [matterId]);

  // Lazy: fetch on first activation, refetch on matter change while active.
  useEffect(() => {
    if (!isActive) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, matterId]);

  const plan = payload?.plan || null;
  const loops = useMemo(() => payload?.loops || [], [payload]);
  const versions = useMemo(() => payload?.versions || [], [payload]);
  const issues = useMemo(() => plan?.issues || [], [plan]);
  const issuesByKey = useMemo(
    () => Object.fromEntries(issues.map((issue) => [issue.key, issue])),
    [issues]
  );
  const activeIssues = useMemo(
    () => issues.filter((issue) => issue.status === "active"),
    [issues]
  );
  const settledIssues = useMemo(
    () => issues.filter((issue) => issue.status !== "active"),
    [issues]
  );

  const handleApprove = useCallback(() => {
    if (!plan || approving) return;
    // The approve belongs to THIS matter. If the user switches matters
    // before the server answers, the answer must not replace what is now
    // on screen; if they merely switch tabs and back, the answer is the
    // newest truth for the matter and beats any refetch that was served
    // before the approval committed (Bugbot, #867).
    const target = matterId;
    setApproving(true);
    approveMatterCasePlan(target, { version: plan.version })
      .then((response) => {
        if (matterRef.current !== target) return;
        // Retire any GET still in flight for this matter: it predates the
        // approval and would paint the draft back over the plan of record.
        requestSeq.current += 1;
        setPayload(response?.data || null);
        setLoadStatus("ready");
        message.success(`Plan v${plan.version} is now the plan of record.`);
      })
      .catch((error) => {
        if (matterRef.current !== target) return;
        const detail = error?.response?.data?.detail;
        message.error(
          detail || "Couldn't approve the plan. Try again in a moment."
        );
        // A 409 means a newer draft landed; show it.
        if (error?.response?.status === 409) load();
      })
      .finally(() => {
        if (matterRef.current === target) setApproving(false);
      });
  }, [approving, load, matterId, plan]);

  const openLetter = useCallback(
    (documentId) => navigate(`/inbox/${documentId}`),
    [navigate]
  );

  if (loadStatus === "idle" || loadStatus === "loading") {
    return (
      <InboxListBody aria-busy="true">
        <BriefingSkeletonGroup>
          <BriefingSkeletonBlock $w="60%" />
          <BriefingSkeletonBlock $w="90%" />
          <BriefingSkeletonBlock $w="75%" />
        </BriefingSkeletonGroup>
      </InboxListBody>
    );
  }

  if (loadStatus === "failed") {
    return (
      <InboxListBody>
        <InboxListErrorBanner role="alert">
          {"Couldn't load this matter's plan. Switch tabs and back to retry."}
        </InboxListErrorBanner>
      </InboxListBody>
    );
  }

  if (!plan) {
    return (
      <InboxListBody>
        <InboxListEmptyState>
          <InboxListEmptyIconRing>
            <Compass size={22} weight="bold" />
          </InboxListEmptyIconRing>
          No plan on this matter yet. The strategy memo writes the first
          draft; it lands here for your approval, and from then on every
          letter is read against it.
        </InboxListEmptyState>
      </InboxListBody>
    );
  }

  const now = ratingText(plan.combined_now);
  const target = (plan.target || "").trim();

  return (
    <InboxListBody data-testid="case-plan-panel">
      <PlanBanner
        plan={plan}
        approving={approving}
        onApprove={handleApprove}
        onOpenLetter={openLetter}
      />

      {plan.summary && <PlanSummary>{plan.summary}</PlanSummary>}

      {(now || target) && (
        <PlanTargetLine data-testid="plan-target">
          {now && (
            <>
              <span>Now</span>
              <strong>{now}</strong>
            </>
          )}
          {now && target && <span>→</span>}
          {target && (
            <>
              <span>Target</span>
              <strong>{target}</strong>
            </>
          )}
        </PlanTargetLine>
      )}

      {loops.length > 0 && (
        <>
          <WorkLaneHeader>Waiting on VA</WorkLaneHeader>
          {loops.map((loop) => (
            <OpenLoopCard key={loop.id} loop={loop} issuesByKey={issuesByKey} />
          ))}
        </>
      )}

      {activeIssues.length > 0 && (
        <>
          <WorkLaneHeader>The plan, issue by issue</WorkLaneHeader>
          {activeIssues.map((issue) => (
            <IssueCard key={issue.key} issue={issue} />
          ))}
        </>
      )}

      {activeIssues.length === 0 && loops.length === 0 && (
        <PlanHint>
          This version names no active issues — the summary above is the whole
          plan.
        </PlanHint>
      )}

      {settledIssues.length > 0 && (
        <>
          <PlanGroupToggle
            type="button"
            onClick={() => setShowSettled((open) => !open)}
            aria-expanded={showSettled}
          >
            {showSettled ? (
              <CaretDown size={12} weight="bold" />
            ) : (
              <CaretRight size={12} weight="bold" />
            )}
            Won, parked and closed ({settledIssues.length})
          </PlanGroupToggle>
          {showSettled &&
            settledIssues.map((issue) => (
              <IssueCard key={issue.key} issue={issue} />
            ))}
        </>
      )}

      <VersionHistory versions={versions} currentId={plan.id} />
    </InboxListBody>
  );
};

CasePlanPanel.propTypes = {
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
};

export default CasePlanPanel;
